import { useArtworkStore } from "@/store/artwork";
import { objExt } from "@xmiles/holopix-helper";
import { ModuleId } from "@xmiles/holopix-types";
import { ref, computed, watch } from "vue";
import type { Ref, ComputedRef } from "vue";

export enum CreationTaskStatus {
  // 排队中
  PENDING = "PENDING",
  // 生成中
  GENERATING = "GENERATING",
  // 生成失败
  FAILED = "FAILED",
  // 生成成功
  SUCCESS = "SUCCESS",
}

interface CreationTaskResult {
  param?: string;
  lastetResult?: any;
}

interface CreationTask {
  clientId: string;
  status: CreationTaskStatus;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
  data?: CreationTaskResult;
}

const STORAGE_KEY = "creation_tasks";
const initialTasks = objExt.parse<CreationTask[]>(
  localStorage.getItem(STORAGE_KEY) || "[]"
);
const tasks: Ref<CreationTask[]> = ref(initialTasks);

watch(
  tasks,
  (newTasks) => {
    localStorage.setItem(STORAGE_KEY, objExt.stringify(newTasks));
  },
  { deep: true }
);

export function useCreationTask() {
  // 添加新任务
  const addTask = (clientId: string): void => {
    const newTask: CreationTask = {
      clientId,
      status: CreationTaskStatus.PENDING,
      isRead: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    // 只存储10条
    if (tasks.value.length >= 10) {
      tasks.value.shift();
    }

    tasks.value.push(newTask);
  };

  // 更新任务状态
  const updateTaskStatus = (
    clientId: string,
    newStatus: CreationTaskStatus
  ): void => {
    const task = tasks.value.find((t) => t.clientId === clientId);
    if (task) {
      task.status = newStatus;
      task.updatedAt = new Date().toISOString();

      // 成功状态时重置为未读
      if (newStatus === CreationTaskStatus.SUCCESS) {
        task.isRead = false;
      }
    }
  };

  // 更新任务最新数据
  const updateTaskData = (
    clientId: string,
    newData?: CreationTaskResult
  ): void => {
    const task = tasks.value.find((t) => t.clientId === clientId);
    if (task) {
      task.data = newData;
      task.updatedAt = new Date().toISOString();
    }
  };

  // 标记任务为已读
  const markAsRead = (clientId: string): void => {
    const task = tasks.value.find((t) => t.clientId === clientId);
    if (task && task.status === CreationTaskStatus.SUCCESS) {
      task.isRead = true;
    }
  };

  // 标记全部完成任务为已读
  const markAllAsRead = (): void => {
    tasks.value.forEach((task) => {
      if (task.status === CreationTaskStatus.SUCCESS) {
        task.isRead = true;
      }
    });
  };

  // 获取所有任务（按更新时间倒序）
  const getAllTasks: ComputedRef<CreationTask[]> = computed(() =>
    [...tasks.value].sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
  );

  // 视频任务列表
  const videoTaskList = computed(() => {
    const artwordStore = useArtworkStore();
    return getAllTasks.value.filter((item) => {
      const params = objExt.parse<any>(item.data?.param);
      const artworkId = params.artworkId;
      return (
        params.module === ModuleId.Aivideo &&
        artworkId === artwordStore.artworkState.id
      );
    });
  });

  // 是否存在生成生成中的视频任务
  const hasGeneratingVideoTask = computed(() => {
    return videoTaskList.value.some(
      (item) => item.status === CreationTaskStatus.GENERATING
    );
  });

  // 是否存在已完成并未读的视频任务
  const hasUnreadSuccessVideoTask = computed(() => {
    return videoTaskList.value.some(
      (item) => item.status === CreationTaskStatus.SUCCESS && !item.isRead
    );
  });

  // 按状态筛选任务
  const getTasksByStatus = (
    status: CreationTaskStatus
  ): ComputedRef<CreationTask[]> =>
    computed(() => tasks.value.filter((task) => task.status === status));

  // 获取成功且未读的任务
  const getUnreadSuccessTasks: ComputedRef<CreationTask[]> = computed(() =>
    tasks.value.filter(
      (task) => task.status === CreationTaskStatus.SUCCESS && !task.isRead
    )
  );

  // 清除所有任务
  const clearAllTasks = (): void => {
    tasks.value = [];
  };

  return {
    tasks,
    addTask,
    updateTaskStatus,
    updateTaskData,
    markAsRead,
    markAllAsRead,
    getAllTasks,
    getTasksByStatus,
    getUnreadSuccessTasks,
    clearAllTasks,
    hasGeneratingVideoTask,
    hasUnreadSuccessVideoTask,
  };
}
